/** @jsxImportSource theme-ui */

import React, { useEffect, useRef, useState, useMemo, Suspense, useCallback } from "react"
import { usePreloadedQuery } from "react-relay"
import graphql from "babel-plugin-relay/macro"
import { useCookies } from "react-cookie"
import _ from "lodash"
import { v4 as uuid } from "uuid"
import {
  usePaginationFragment,
  useMutation,
  useQueryLoader,
  useSubscription,
  useRelayEnvironment,
} from "react-relay"
import { ConnectionHandler, fetchQuery } from "relay-runtime"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faCog,
  faPaperPlane,
  faPaperclip,
  faCircleNotch,
  faArrowUp,
  faTimes
} from "@fortawesome/free-solid-svg-icons"
import Color from "color"
import $ from "jquery"
import Select from "react-select"
import { Helmet } from "react-helmet"
import { LoadingMask } from "../../CommonStyles"
import ChatVisualizer from "../ChatVisualizer/ChatVisualizer"
import PersistentMenu from "../PersistentMenu/PersistentMenu"
import BusinessInfoPreview from "../BusinessInfoPreview/BusinessInfoPreview"
import {
  ConversationHistoryCreateCustomerMutation,
  ConversationHistoryCreateChatMutation,
  ConversationHistoryUpdateCustomerLocaleMutation,
  ConversationHistorySignUrlMutation,
  ConversationHistoryQuery,
  ConversationHistorySubscription
} from "./ConversationHistoryQuery"

import * as Styles from "./ConversationHistoryStyles"
import { localeMatcher } from "../util"

const ROUTER_BASE = process.env.REACT_APP_ROUTER_BASE
const INSECURE = process.env.REACT_APP_COOKIES_INSECURE ? JSON.parse(process.env.REACT_APP_COOKIES_INSECURE) : false
const LUM_THRESHOLD = 0.6
const COOKIE_MAX_AGE = 90 * 24 * 60 * 60

const InnerConversationHistory = ({
  info,
  customerToken,
  onChangeCustomerToken,
  onPostMessageToSDK,
  isRefetching,
  ...props
}) => {
  // const environment = useRelayEnvironment()
  const {
    data,
    loadPrevious,
    hasPrevious,
    isLoadingPrevious,
  } = usePaginationFragment(
    graphql`
      fragment ConversationHistory_customerViewer on CustomerScope
      @argumentDefinitions(
        last: { type: "Int", defaultValue: 50 }
        before: { type: "String" }
        token: { type: "ID" }
      )
      @refetchable(queryName: "ConversationHistoryPagination") {
        chats (
          last: $last,
          before: $before,
          token: $token,
        ) 
        @connection(key: "ConversationHistory_chats") {
          edges {
            node {
              _id
              messageEvent
              hidden
              from
              createdAt
              deliveredAt
              readAt
              sentAt
              threadId
              clientMutationId
              sender
              customerId
              agentId
            }
          }
        }
        defaultToken
      }
    `,
    props.data.customerViewer
  )

  const customer = props.data?.customerViewer?.customerProfile || null
  const themeColor = _.get(info, "customerViewer.chatConfiguration.themeColor", "#173753")
  const propsChats = (data?.chats?.edges || []).map(o => o.node)
  const [loadingMoreChats, setLoadingMoreChats] = useState(false)
  const [showGetStarted, setShowGetStarted] = useState(customer?.locale ? false : true)
  const [showSettings, setShowSettings] = useState(false)
  const [headerShowOverflow, setHeaderShowOverflow] = useState(false)
  const [selectedTab, setSelectTab] = useState(0)
  const [textArea, setTextArea] = useState("")
  const [botTyping, setBotTyping] = useState(false)
  const [headerShowProfileOverflow, setHeaderShowProfileOverflow] = useState(false)
  const [selectedLocaleGroup, setSelectedLocaleGroup] = useState("")
  const [defaultLocale, setDefaultLocale] = useState(null)
  const [referral, setReferral] = useState(null)
  const [createCustomerError, setCreateCustomerError] = useState(null)
  const [chats, setChats] = useState(propsChats)
  const [localCXToken, setLocalCXToken] = useState(customerToken || null)
  const [fontSize, setFontSize] = useState(50)
  const [creatingFirstChat, setCreatingFirstChat] = useState(false)
  const [cookies, setCookie] = useCookies([])
  const [commitCustomer, isInFlightCustomer] = useMutation(
    ConversationHistoryCreateCustomerMutation
  )
  const [commitChat, isInFlightChat] = useMutation(
    ConversationHistoryCreateChatMutation
  )
  const [commitCustomerLocale, isInFlightCustomerLocale] = useMutation(
    ConversationHistoryUpdateCustomerLocaleMutation
  )
  const [commitSignUrl, isInFlightSignUrl] = useMutation(
    ConversationHistorySignUrlMutation
  )

  useEffect(() => {
    if (cookies) {
      const cookiesFontSize = cookies["font-size"]
      if (cookiesFontSize) {
        $("html").css("font-size", cookiesFontSize)
        if (cookiesFontSize === "12px") {
          setFontSize(0)
        } else if (cookiesFontSize === "22px") {
          setFontSize(100)
        } else {
          setFontSize(50)
        }

      }
    }
  }, [])

  const memoizedValue = useMemo(() => {
    if (localCXToken) {
      return {
        variables: {
          token: localCXToken,
        },
        subscription: ConversationHistorySubscription,
        onNext: (response) => {
          const newChat = response?.chat
          // only handle for first chat
          if (chats?.length === 1) {
            const newChat = response?.chat
            const modChats = [
              ...chats,
              newChat
            ]
            setChats(modChats)
          }
          if (newChat?.messageEvent?.type === "WEB_ACTION") {
            onPostMessageToSDK({
              event: "WEB_ACTION",
              data: newChat?.messageEvent?.data
            })
          }
        },
        updater: (store, data) => {
          setBotTyping(false)
          const customerViewer = store.getRoot().getLinkedRecord("customerViewer")
          const chatsConnection = ConnectionHandler.getConnection(
            customerViewer,
            "ConversationHistory_chats",
            { token: localCXToken }
          )
          const newChat = store.getRootField("chat")
          if (newChat && chatsConnection) {
            const edge = ConnectionHandler.createEdge(store, chatsConnection, newChat, "ChatEdge")
            ConnectionHandler.insertEdgeAfter(chatsConnection, edge)
          }
        },
        onError: (error) => {
          console.log("memoized value error", error)
        }
      }
    }
    return {
      variables: {
        token: "",
      },
      subscription: ConversationHistorySubscription
    }
  }, [
    localCXToken, 
    (chats?.length === 1), 
    ConversationHistorySubscription
  ])

  useSubscription(memoizedValue)

  useEffect(() => {
    const newChatsFromProps = _.differenceBy(propsChats, chats, "_id")
    let clonedChats = _.cloneDeep(chats)
    const chatsToUnshift = []
    newChatsFromProps.forEach((o, index) => {
      if (o?.clientMutationId) {
        const targetIndex = chats.findIndex(c => c.clientMutationId === o.clientMutationId)
        if (targetIndex > -1) {
          clonedChats[targetIndex] = o
        } else {
          clonedChats.push(o)
        }
      } else if (o.createdAt < clonedChats?.[0]?.createdAt) {
        // When new chats are from loading previous conversation history, push to array chatsToUnshift to retain the order and unshift them afterwards
        chatsToUnshift.push(o)
      } else {
        clonedChats.push(o)
      }
    })
    if (chatsToUnshift?.length) {
      clonedChats.unshift(...chatsToUnshift)
    }
    setChats(clonedChats)
  }, [JSON.stringify(propsChats.map(o => o?._id))])

  useEffect(() => {
    let selectedLg
    if (customer?.locale) {
      selectedLg = selectedLg = (info?.customerViewer?.localeGroups || []).find((lg) => {
        const locales = lg?.locales ?? []
        const matchedlocale = locales.find(o => localeMatcher(o, customer?.locale))
        return !!matchedlocale
      })
    }
    if (!selectedLg && info?.customerViewer?.localeGroups?.length) {
      const { localeGroup } = getLocale({
        ...info?.customerViewer,
        defaultLocale
      })
      if (localeGroup) {
        selectedLg = localeGroup
      } else {
        selectedLg = info?.customerViewer?.localeGroups.find(o => o.default)
      }
    }
    setSelectedLocaleGroup(selectedLg)
  }, [JSON.stringify(info?.customerViewer?.localeGroups), defaultLocale])

  useEffect(() => {
    if (!_.isEmpty(chats)) {
      setShowGetStarted(false)
    }
  }, [JSON.stringify(chats)])

  const handleEvent = (e) => {
    if (e?.data?.source === "woztell-web-chat-parent") {
      const payload = e.data.payload
      switch (payload.event) {
        case "INIT": {
          const {
            locale,
            ref,
          } = payload.data
          if (locale) setDefaultLocale(locale)
          if (ref) setReferral(ref)
          break
        }
        case "WEB_EVENT":
          sendEvent({
            token: localCXToken,
            type: "WEB_EVENT",
            data: payload.data,
            hidden: true,
          })
          break
        default:
          break
      }
    }
  }

  useEffect(() => {
    window.addEventListener("message", handleEvent)
    return () => {
      window.removeEventListener("message", handleEvent)
    }
  }, [localCXToken])

  const getLocale = ({
    localeGroups = [],
    defaultLocale,
  }) => {
    let browserLocales

    if (navigator.languages && navigator.languages.length) {
      browserLocales = navigator.languages
    } else {
      browserLocales = [navigator.userLanguage || navigator.language || navigator.browserLanguage || navigator.systemLanguage || "default"]
    }
    if (localeGroups?.length) {
      if (defaultLocale) {
        const match = localeGroups.find((lg) => {
          const locales = lg?.locales ?? []
          const matchedlocale = locales.find(o => localeMatcher(o, defaultLocale))
          return !!matchedlocale
        })
        if (match) {
          return {
            locale: match.locales?.[0],
            localeGroup: match,
          }
        }
        return {
          locale: defaultLocale,
          localeGroup: null,
        }
      }
      for (let i = 0; i < browserLocales.length; i++) {
        const locale = browserLocales[i]
        const match = localeGroups.find((lg) => {
          const locales = lg?.locales ?? []
          // console.log("locales", locales)
          const matchedlocale = locales.find(o => localeMatcher(o, locale))
          // console.log("matchedlocale", matchedlocale)
          return !!matchedlocale
        })
        // console.log("match", match)
        if (match) {
          return {
            locale: match.locales?.[0],
            localeGroup: match,
          }
        }
      }
    }
    return {
      locale: browserLocales[0],
      localeGroup: null,
    }
  }

  const body = useRef(null)
  const botTypingRef = useRef(null)
  const chatVisualizerRef = useRef(null)

  const loadMoreButton = () => {
    if (!chats?.length) {
      return null
    }
    if (isLoadingPrevious) {
      return (
        <Styles.LoadMoreConversation>
          <FontAwesomeIcon
            icon={faCircleNotch}
            spin
          />
        </Styles.LoadMoreConversation>
      )
    }
    if (hasPrevious) {
      return (
        <Styles.LoadMoreConversation
          onClick={() => {
            setLoadingMoreChats(true)
            const oldScrollTop = _.clone(chatVisualizerRef.current?.offsetHeight)
            loadPrevious(
              10,
              {
                onComplete: (error) => {
                  setLoadingMoreChats(false)
                  if (error) {
                    console.log("error", error)
                  } else {
                    const newScrollTop = chatVisualizerRef.current?.offsetHeight
                    if (body?.current) {
                      body.current.scrollTop = newScrollTop - oldScrollTop
                    }
                  }
                }
              }
            )
          }}
        >
          <FontAwesomeIcon
            className="icon"
            icon={faArrowUp}
          />
          <span>Tap to load more conversation...</span>
        </Styles.LoadMoreConversation>
      )
    }
    return (
      <Styles.LoadMoreConversation>Start of conversation</Styles.LoadMoreConversation>
    )
  }

  const createFirstChat = ({ businessInfo, data }) => {
    commitCustomer({
      variables: {
        input: {
          businessId: businessInfo?.businessId,
          orgId: businessInfo?.orgId,
          profile: {
            locale: selectedLocaleGroup?.locales[0]
          }
        },
      },
      onCompleted: (response, errs) => {
        if (response?.createCustomer?.error) {
          setCreateCustomerError(response?.createCustomer?.error)
          return
        }
        setCreateCustomerError(null)
        const at = response?.createCustomer?.accessToken
        setLocalCXToken(at)
        if (cookies) {
          setCookie(`customerToken_${info?.customerViewer.businessId}`, at, {
            path: "/",
            sameSite: INSECURE ? "lax" : "none",
            secure: !INSECURE,
            maxAge: COOKIE_MAX_AGE,
          })
        }
        const clientMutationId = uuid()
        const messageEvent = {
          type: data.type,
          data: data.data,
          hidden: false,
        }
        if (referral) {
          _.set(messageEvent, "data.referral", { ref: referral })
        }
        const modChats = [
          {
            createdAt: new Date().getTime(),
            messageEvent,
            clientMutationId
          }
        ]
        setCreatingFirstChat(true)
        setChats(modChats)
        setShowGetStarted(false)
        setTimeout(() => {
          sendEvent({
            firstChat: true,
            token: at,
            type: data.type,
            data: data.data,
            firstChatMessage: modChats[0]
          })
        }, 3000)
      },
      onError: (err) => {
        console.log(err)
        setCreateCustomerError({ message: err?.message })
      }
    })
  }

  const uploadFiles = async (selectedFiles) => {
    const signUrl = (file) => {
      return new Promise((resolve, reject) => {
        commitSignUrl({
          variables: {
            input: {
              fileName: file.name,
              fileType: file.type,
              token: customerToken,
            },
          },
          onCompleted: (response, errs) => {
            resolve(response?.signUrl?.signUrl)
          },
          onError: (err) => {
            console.log(err)
            reject(err)
          }
        })
      })
    }
    const urls = await Promise.all(selectedFiles.map(file => signUrl(file)))
    try {
      const uploads = await Promise.all(urls.map((o, i) => {
        const form = new FormData()
        _.forEach(o.fields, (value, key) => {
          form.append(key, value)
        })
        form.append("file", selectedFiles[i])
        return fetch(o.url, { method: "POST", body: form })
          .then((res) => {
            if (res.status === 204) {
              const split = o.fields.key.split("/")
              const encodedSplit = split.map(o => encodeURIComponent(o))
              return Promise.resolve({
                url: `${o.url}/${encodedSplit.join("/")}`,
                fileName: split[split.length - 1],
                fileType: selectedFiles[i].type,
                size: selectedFiles[i].size,
              })
            }
            return null
          })
      }))
      let fileType = "FILE"
      // console.log("uploads", uploads)
      uploads.forEach((obj) => {
        switch (obj.fileType) {
          case "image/png":
          case "image/jpeg":
          case "image/gif":
          case "image/bmp":
          case "image/vnd.microsoft.icon":
          case "image/image/svg+xml":
          case "image/tiff":
          case "image/webp":
            fileType = "IMAGE"
            break
          case "audio/aac":
          case "audio/x-midi":
          case "audio/midi":
          case "audio/mpeg":
          case "audio/ogg":
          case "audio/opus":
          case "audio/wav":
          case "audio/webm":
          case "audio/3gpp":
          case "audio/3gpp2":
          case "audio/x-m4a":
            fileType = "AUDIO"
            break
          case "video/x-msvideo":
          case "video/mpeg":
          case "video/ogg":
          case "video/mp2t":
          case "video/webm":
          case "video/3gpp":
          case "video/3gpp2":
          case "video/quicktime":
            fileType = "VIDEO"
            break
          default:
            break
        }
        sendEvent({
          token: customerToken,
          type: fileType,
          data: {
            attachments: [{
              type: fileType,
              url: obj.url
            }]
          }
        })
      })
    } catch (err) {

    }
  }

  const textInputEls = ({
    persistentMenu,
    themeColor
  }) => {
    let result = [
      (
        <Styles.TextField
          key="text-input"
          async
          placeholder="Send a message..."
          onChange={(e) => {
            setTextArea(e.target.value)
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              if (!e.shiftKey) {
                e.preventDefault()
                if (textArea) {
                  if (creatingFirstChat) {
                    return
                  }
                  setTextArea("")
                  let text = textArea
                  if (/\n$/.test(textArea)) {
                    text = textArea.slice(0, -1)
                  }
                  if (_.isEmpty(customer)) {
                    createFirstChat({
                      businessInfo: info?.customerViewer,
                      data: {
                        type: "TEXT",
                        data: {
                          text
                        }
                      }
                    })
                  } else {
                    sendEvent({
                      token: customerToken,
                      type: "TEXT",
                      data: {
                        text
                      }
                    })
                  }
                }
              }
            }
          }}
          value={textArea}
        />
      ),
      (
        <Styles.UploadButton
          key="upload-button"
          color={themeColor}
          disabled={isInFlightChat || isInFlightSignUrl || creatingFirstChat}
        >
          <label htmlFor="upload-file">
            <FontAwesomeIcon
              className="icon"
              icon={isInFlightSignUrl ? faCircleNotch : faPaperclip}
              spin={isInFlightSignUrl ? true : false}
              fixedWidth
            />
            <Styles.FileInput
              type="file"
              id="upload-file"
              // ref={c => this.fileInput = c}
              multiple
              onChange={(e) => {
                // console.log("f", e.target.files)
                // look like ...e.target.files (spreading file list broken)
                const failed = Array.prototype.filter.call(e.target.files, f => f.size > 20.48 * 1000 * 1000)
                const selected = Array.prototype.filter.call(e.target.files, f => f.size <= 20.48 * 1000 * 1000)
                if (failed.length === 0 && selected.length === 0) {
                  e.target.files.forEach((f) => {
                    if (f.size <= 20.48 * 1000 * 1000) {
                      selected.push(f)
                    } else {
                      failed.push(f)
                    }
                  })
                }

                if (failed.length) {
                  const errorMessage = failed.reduce((memo, file) => {
                    return `${memo}${file.name}\n`
                  }, "The following files exceed the 20MB size limit:\n")
                  alert(errorMessage)
                }

                if (selected.length) {
                  uploadFiles(selected)
                }
              }}
            />
          </label>
        </Styles.UploadButton>
      ),
      (
        <Styles.SendButton
          key="send-button"
          color={themeColor}
          onClick={() => {
            if (textArea) {
              if (_.isEmpty(customer)) {
                createFirstChat({
                  businessInfo: info?.customerViewer,
                  data: {
                    type: "TEXT",
                    data: {
                      text: textArea
                    }
                  }
                })
              } else {
                sendEvent({
                  token: customerToken,
                  type: "TEXT",
                  data: {
                    text: textArea
                  }
                })
              }
              setTextArea("")
            }
          }}
          disabled={textArea === "" || creatingFirstChat}
        >
          <FontAwesomeIcon className="icon" icon={faPaperPlane} fixedWidth />
        </Styles.SendButton>
      )
    ]
    if (persistentMenu?.enable && persistentMenu?.buttons.length >= 1) {
      result = [
        (<PersistentMenu
          key="persistent-menu"
          color={themeColor}
          persistentMenu={persistentMenu}
          disabled={creatingFirstChat}
          onAction={(action) => {
            if (action.type === "postback") {
              if (_.isEmpty(customer)) {
                createFirstChat({
                  businessInfo: info?.customerViewer,
                  data: {
                    type: "PAYLOAD",
                    data: {
                      text: action.title,
                      payload: action.payload
                    }
                  }
                })
              } else {
                sendEvent({
                  token: customerToken,
                  type: "PAYLOAD",
                  data: {
                    text: action.title,
                    payload: action.payload
                  }
                })
              }
            }
          }}
        />
        ),
        ...result
      ]
    }
    return result
  }

  const insertErrorToChats = ({ clientMutationId, error, chats }) => {
    let failed = chats.find((o) => {
      return o.clientMutationId === clientMutationId
    })
    const index = chats.findIndex(o => o.clientMutationId === clientMutationId)
    failed.error = error
    const modChats = [
      ...chats.slice(0, index),
      failed,
      ...chats.slice(index + 1)
    ]
    return modChats
  }

  const sendEvent = ({
    firstChat = false,
    token,
    type,
    data,
    hidden = false,
    currentChats = [],
    firstChatMessage = null
  }) => {
    const messageEvent = {
      type,
      data,
      hidden,
    }
    const clientMutationId = uuid()
    let stateChats = chats
    if (currentChats?.length) {
      stateChats = currentChats
    }
    let modChats = stateChats
    if (!firstChat) {
      modChats = [
        ...stateChats,
        {
          createdAt: new Date().getTime(),
          messageEvent,
          clientMutationId
        }
      ]
      setChats(modChats)
    }
    const mutationParams = {
      variables: {
        input: {
          messageEvent: firstChat ? firstChatMessage.messageEvent : messageEvent,
          token,
          clientMutationId: firstChat ? firstChatMessage.clientMutationId : clientMutationId
        },
      },
      updater: (store) => {
        const rootField = store.getRootField("createChat")
        const error = rootField.getLinkedRecord("error")
        if (!error) {
          const clientMutationId = rootField.getValue("clientMutationId")
          const customerViewer = store.getRoot().getLinkedRecord("customerViewer")
          let chatsConnection
          if (firstChat) {
            chatsConnection = ConnectionHandler.getConnection(
              customerViewer,                        // parent record
              "ConversationHistory_chats",   // connection key
            )
          } else {
            chatsConnection = ConnectionHandler.getConnection(
              customerViewer,                        // parent record
              "ConversationHistory_chats",   // connection key
              { token }
            )
          }
          const newChat = rootField.getLinkedRecord("chat")
          newChat.setValue(clientMutationId, "clientMutationId")
          const edge = ConnectionHandler.createEdge(store, chatsConnection, newChat, "ChatEdge")
          ConnectionHandler.insertEdgeAfter(chatsConnection, edge)
        }
      },
      onCompleted: (response, errs) => {
        if (!response.createChat?.error) {
          setBotTyping(true)
          if (botTypingRef) {
            clearTimeout(botTypingRef)
          }
          botTypingRef.current = setTimeout(() => {
            setBotTyping(false)
            botTypingRef.current = null
          }, 10000)
        } else {
          const result = insertErrorToChats({ clientMutationId, error: response?.createChat?.error, chats: modChats })
          setChats(result)
        }
        if (firstChat) {
          onChangeCustomerToken(token)
          setCreatingFirstChat(false)
        }
      },
      onError: (err) => {
        console.log(err)
        const result = insertErrorToChats({
          clientMutationId,
          error: {
            message: err?.message
          },
          chats: modChats
        })
        setChats(result)
      }
    }
    commitChat(mutationParams)

  }

  const footer = ({
    businessInfo,
    themeColor
  }) => {
    const localised = businessInfo?.chatConfiguration?.localised || []
    let selectedLocalised
    if (localised.find(o => o.localeId === selectedLocaleGroup.id)) {
      selectedLocalised = localised.find(o => o.localeId === selectedLocaleGroup.id)
    } else {
      const defaultLg = businessInfo?.localeGroups?.find(o => o.default)
      selectedLocalised = localised.find(o => o.localeId === defaultLg.id)
    }
    return (
      <Styles.Footer className="chat-footer" loadingOverlay={isInFlightCustomer || isRefetching}>
        <div className="overlay"></div>
        <Styles.CreateCustomerErrorContainer
          className="create-cutomer-error-container"
        >
          <Styles.ErrorMessage
            className="create-cutomer-error-message"
            open={createCustomerError}
          >
            {!_.isEmpty(createCustomerError) ?
              <>{`[${createCustomerError?.code}] ${createCustomerError?.message}`}</>
              : null
            }
          </Styles.ErrorMessage>
        </Styles.CreateCustomerErrorContainer>
        <Styles.IceBreakersContainer className="ice-breakers-container">
          {!!showGetStarted && !isRefetching && selectedLocalised?.iceBreakers?.enable && !!selectedLocalised?.iceBreakers?.ctas?.length && (
            <>
              {(selectedLocalised?.iceBreakers?.ctas || []).map((iceBreaker, i) => {
                if (iceBreaker.question) {
                  return (
                    <Styles.IceBreakerButton
                      key={`${i}_${iceBreaker.payload}`}
                      className="chat-ice-breaker-button"
                      themeColor={themeColor}
                      onClick={() => {
                        const data = {
                          type: "PAYLOAD",
                          data: {
                            text: iceBreaker.question,
                            payload: iceBreaker.payload
                          }
                        }
                        createFirstChat({ businessInfo, data: data })
                      }}
                    >
                      {iceBreaker.question}
                    </Styles.IceBreakerButton>
                  )
                }
                return null
              })}
            </>
          )}
        </Styles.IceBreakersContainer>
        <Styles.ActionRow className="action-row">
          <div className="action-wrapper">
            {textInputEls({ themeColor, persistentMenu: selectedLocalised?.persistentMenu })}
          </div>
        </Styles.ActionRow>
      </Styles.Footer>
    )
  }

  const header = ({
    businessInfo,
    themeColor,
    defaultToken,
  }) => {
    let inIFrame
    try {
      inIFrame = window.self !== window.top
    } catch (e) {
      inIFrame = true
    }
    return (
      <Styles.Header
        className="chat-header"
        color={themeColor}
        open={showSettings}
        showOverflow={headerShowOverflow}
        onTransitionEnd={() => {
          if (showSettings) {
            setHeaderShowOverflow(true)
          }
        }}
      >
        <Styles.BotProfileBar
          className="chat-header-bar"
          color={themeColor}
        >
          <div className="align-left">
            {inIFrame && (
              <Styles.CloseButton
                className="chat-header-close-button"
                color={themeColor}
                onClick={() => {
                  onPostMessageToSDK({
                    event: "CLOSE_WEBCHAT"
                  })
                }}
              >
                <FontAwesomeIcon
                  className="chat-header-close-icon"
                  icon={faTimes}
                  fixedWidth
                />
              </Styles.CloseButton>
            )}
            <div className="chat-header-profile">
              <Styles.ProfilePic
                className="chat-header-profile-pic"
                url={_.get(businessInfo, "profile.logoUrl")}
                color={themeColor}
                onClick={() => {
                  setHeaderShowProfileOverflow(!headerShowProfileOverflow)
                }}
              />
              <div
                className="chat-header-name"
                onClick={() => {
                  setHeaderShowProfileOverflow(!headerShowProfileOverflow)
                }}
              >
                {businessInfo?.profile?.name}
              </div>
            </div>
          </div>
          <div className="align-right">
            <Styles.SettingsButton
              className="chat-header-settings-button"
              color={themeColor}
              onClick={() => {
                if (showSettings) {
                  setHeaderShowOverflow(false)
                  setShowSettings(false)
                } else {
                  setShowSettings(true)
                }
              }}
            >
              <FontAwesomeIcon
                className="chat-header-settings-icon"
                icon={faCog}
                fixedWidth
                spin
              />
            </Styles.SettingsButton>
            {inIFrame && (
              <Styles.PopupButton
                className="chat-header-popup-button"
                color={themeColor}
                target="_blank"
                href={`${ROUTER_BASE}/messenger/?token=${defaultToken}`}
              >
                <Styles.PopupIcon
                  color={themeColor}
                  className="chat-header-popup-icon"
                />
              </Styles.PopupButton>
            )}
          </div>
        </Styles.BotProfileBar>
        <Styles.Settings>
          <Styles.TabBar color={themeColor}>
            <Styles.Tab
              color={themeColor}
              selected={selectedTab === 0}
              onClick={() => {
                setSelectTab(0)
              }}
            >
              <Styles.LanguageIcon color={themeColor} />
            </Styles.Tab>
            <Styles.Tab
              color={themeColor}
              selected={selectedTab === 1}
              onClick={() => {
                setSelectTab(1)
              }}
            >
              <Styles.FontSizeIcon color={themeColor} />
            </Styles.Tab>
          </Styles.TabBar>
          {tabContent({ themeColor, localeGroups: businessInfo?.localeGroups })}
        </Styles.Settings>
      </Styles.Header>
    )
  }

  const tabContent = ({ themeColor, localeGroups }) => {
    const langOptions = (localeGroups || []).map(o => ({
      label: o.name,
      value: o.id
    }))
    const color = Color(themeColor)
    const markStyle = {
      fontSize: "0.8rem"
    }
    if (color.luminosity() <= LUM_THRESHOLD) {
      markStyle.color = "#ffffff"
    }
    switch (selectedTab) {
      case 0:
        return (
          <Styles.TabContent
            color={themeColor}
          >
            <div className="field">
              {/* <div className="label">Language</div> */}
              <Select
                menuPosition="fixed"
                className="lang-selector"
                // isDisabled={_.isEmpty(member)}
                loading={isInFlightCustomerLocale}
                options={langOptions}
                value={langOptions.find(o => o.value === selectedLocaleGroup.id)}
                onChange={(value) => {
                  const targetLG = (info?.customerViewer?.localeGroups || []).find(o => o.id === value.value)
                  setSelectedLocaleGroup(targetLG)
                  if (!_.isEmpty(customer)) {
                    const customLocale = targetLG?.locales[0]
                    commitCustomerLocale({
                      variables: {
                        input: {
                          locale: customLocale,
                          token: customerToken
                        },
                      },
                      onCompleted: (response, errs) => {
                      },
                      onError: (err) => {
                        console.log(err)
                      }
                    })
                  }
                }}
              />
            </div>
          </Styles.TabContent>
        )
      case 1:
        return (
          <Styles.TabContent
            color={themeColor}
          >
            <div className="field">
              {/* <div className="label">Font Size</div> */}
              <Styles.SegmentedControl
                color={themeColor}
              >
                <Styles.Segment
                  selected={fontSize === 0}
                  color={themeColor}
                  onClick={() => {
                    $("html").css("font-size", "12px")
                    if (cookies) {
                      setFontSize(0)
                      setCookie("font-size", "12px", {
                        path: "/",
                        sameSite: INSECURE ? "lax" : "none",
                        secure: !INSECURE,
                        maxAge: COOKIE_MAX_AGE,
                      })
                    }
                  }}
                >
                  <Styles.SFont
                    selected={fontSize === 0}
                    color={themeColor}
                  />
                </Styles.Segment>
                <Styles.Segment
                  selected={fontSize === 50}
                  color={themeColor}
                  onClick={() => {
                    $("html").css("font-size", "16px")
                    if (cookies) {
                      setFontSize(50)
                      setCookie("font-size", "16px", {
                        path: "/",
                        sameSite: INSECURE ? "lax" : "none",
                        secure: !INSECURE,
                        maxAge: COOKIE_MAX_AGE,
                      })
                    }
                  }}
                >
                  <Styles.MFont
                    selected={fontSize === 50}
                    color={themeColor}
                  />
                </Styles.Segment>
                <Styles.Segment
                  selected={fontSize === 100}
                  color={themeColor}
                  onClick={() => {
                    $("html").css("font-size", "22px")
                    if (cookies) {
                      setFontSize(100)
                      setCookie("font-size", "22px", {
                        path: "/",
                        sameSite: INSECURE ? "lax" : "none",
                        secure: !INSECURE,
                        maxAge: COOKIE_MAX_AGE,
                      })
                    }
                  }}
                >
                  <Styles.LFont
                    selected={fontSize === 100}
                    color={themeColor}
                  />
                </Styles.Segment>
              </Styles.SegmentedControl>
            </div>
          </Styles.TabContent>
        )

      default:
        return null
    }
  }
  return (
    <>
      <Helmet>
        <style type="text/css">{`
            ${info?.customerViewer?.chatConfiguration?.customCss}
          `}
        </style>
      </Helmet>
      <Styles.Chat
        className="chat"
      >
        <Styles.Container
          className="chat-container"
          color={themeColor}
        >
          <BusinessInfoPreview
            themeColor={themeColor}
            info={info?.customerViewer}
            showOverflow={headerShowProfileOverflow}
            onChange={(value) => {
              setHeaderShowProfileOverflow(value)
            }}
          />
          {header({ businessInfo: info?.customerViewer, themeColor, defaultToken: data?.defaultToken })}
          <div className="content">
            <Styles.Body ref={body}>
              {loadMoreButton()}
              <ChatVisualizer
                chatVisualizerRef={chatVisualizerRef}
                loadingHistory={loadingMoreChats}
                chats={chats.filter(o => !o.hidden)}
                botProfilePic={info?.customerViewer?.chatConfiguration?.showAgentProfile ? _.get(info, "customerViewer.profile.botAgentImageUrl") : _.get(info, "customerViewer.profile.logoUrl")}
                themeColor={themeColor}
                onAction={(action) => {
                  sendEvent({
                    token: customerToken,
                    type: "PAYLOAD",
                    data: _.omit(action, ["type"])
                  })
                }}
                onResend={({ clientMutationId, messageEvent }) => {
                  const modChats = chats.filter(o => o.clientMutationId !== clientMutationId)
                  setChats(modChats)
                  if (_.isEmpty(customer)) {
                    createFirstChat({
                      businessInfo: info?.customerViewer,
                      data: {
                        type: messageEvent?.type,
                        data: messageEvent?.data
                      }
                    })
                  } else {
                    sendEvent({
                      token: customerToken,
                      type: messageEvent?.type,
                      data: messageEvent?.data,
                      currentChats: modChats
                    })
                  }
                }}
              />
              {botTyping && (
                <Styles.TypingRow
                  className="bot row"
                  ref={(c) => {
                    if (c) {
                      setTimeout(() => {
                        c.scrollIntoView({
                          block: "end",
                          behavior: "smooth"
                        })
                      }, 500)
                    }
                  }}
                >
                  <Styles.BotProfilePic
                    url={info?.customerViewer?.chatConfiguration?.showAgentProfile ? _.get(info, "customerViewer.profile.botAgentImageUrl") : _.get(info, "customerViewer.profile.logoUrl")}
                    color={themeColor}
                  />
                  <div className="content-info">
                    <div className="content">
                      <div>
                        <Styles.TypingContainer>
                          <span className="dot"></span>
                          <span className="dot"></span>
                          <span className="dot"></span>
                        </Styles.TypingContainer>
                      </div>
                    </div>
                  </div>
                </Styles.TypingRow>
              )}
            </Styles.Body>
            {footer({ businessInfo: info?.customerViewer, themeColor })}
          </div>
        </Styles.Container>
      </Styles.Chat>
    </>
  )
}

const InnerWrapper = ({
  query,
  queryRef,
  ...props
}) => {
  const data = usePreloadedQuery(
    query,
    queryRef,
  )

  return (
    <InnerConversationHistory
      data={data}
      {...props}
    />
  )
}

const ConversationHistory = ({
  initialQueryRef,
  info,
  onPostMessageToSDK,
}) => {
  const [
    queryReference,
    loadQuery,
  ] = useQueryLoader(
    ConversationHistoryQuery,
    initialQueryRef, /* e.g. provided by router */
  )
  const firstLoad = useRef(false)
  const environment = useRelayEnvironment()
  const [cookies] = useCookies([])
  let token = null
  if (cookies[`customerToken_${info?.customerViewer?.businessId}`]) {
    token = cookies[`customerToken_${info.customerViewer.businessId}`]
  }
  const [customerToken, setCustomerToken] = useState(token)
  const [isRefetching, setIsRefetching] = useState(false)

  const refetch = useCallback(() => {
    if (isRefetching) { return }
    setIsRefetching(true)

    // fetchQuery will fetch the query and write
    // the data to the Relay store. This will ensure
    // that when we re-render, the data is already
    // cached and we don't suspend
    fetchQuery(environment, ConversationHistoryQuery, {
      last: 50,
      token: customerToken,
    })
      .subscribe({
        complete: () => {
          setIsRefetching(false)

          // *After* the query has been fetched, we call
          // loadQuery again to re-render with a new
          // queryRef.
          // At this point the data for the query should
          // be cached, so we use the 'store-only'
          // fetchPolicy to avoid suspending.
          loadQuery({ last: 50, token: customerToken }, { fetchPolicy: 'store-only' })
        },
        error: () => {
          setIsRefetching(false)
        }
      })
  }, [customerToken])

  useEffect(() => {
    if (info?.customerViewer?.businessId) {
      loadQuery({
        last: 50,
        token: customerToken,
      })
    }
  }, [info?.customerViewer?.businessId])

  useEffect(() => {
    if (!firstLoad.current) {
      firstLoad.current = true
    } else if (customerToken) {
      refetch()
    }
  }, [customerToken])

  if (queryReference) {
    return (
      <Suspense fallback={(
        <LoadingMask>
          <div className="line"></div>
          <div className="line"></div>
          <div className="line"></div>
        </LoadingMask>
      )}>
        <InnerWrapper
          queryRef={queryReference}
          query={ConversationHistoryQuery}
          isRefetching={isRefetching}
          info={info}
          customerToken={customerToken}
          onChangeCustomerToken={(token) => setCustomerToken(token)}
          onPostMessageToSDK={onPostMessageToSDK}
        />
      </Suspense>
    )
  }
  return null
}

export default ConversationHistory